/* What needs somebody's attention, in one list.
 *
 * Warnings used to live in four places: the audit log (sync differences, renames, failures), the
 * dashboard's status tile, the Expiries screen and the Monday digest email. Each is right on its
 * own, and none of them answers "is there anything I should be looking at?" without opening all
 * four. This builds that answer from data the app already holds, and nothing here is stored: it is
 * recomputed from the registers, so an item disappears the moment its cause does.
 *
 * Pure, like expiries.ts. Takes fetched collections, returns rows.
 *
 * WHAT MAKES AN ITEM THE SAME ITEM
 *
 * Each item has an id, and the id is what a dismissal remembers. It must change when the situation
 * does, or dismissing "3 devices not encrypted" would silence a fourth one for ever. So the ids of
 * counted items carry the set they counted (a hash of the members), and the ids of single events
 * carry the date they happened.
 */

import type { IAuditEntry, IDevice, IExpiry, IPerson, IStatusTile } from '../models';
import { recordHref } from './recordHref';
import { groupOf } from './deviceGroups';
import { fmtDate } from './format';
import { isOffboarding, isOnboarding, needsWelcome } from './lifecycle';

export type AttentionSeverity = 'bad' | 'warn' | 'info';
export type AttentionGroup = 'Sync' | 'Devices' | 'People' | 'Expiries';

export interface AttentionItem {
  id: string;
  severity: AttentionSeverity;
  group: AttentionGroup;
  icon: string;
  title: string;
  detail: string;
  /** Where to go to deal with it. */
  to: string;
}

export interface AttentionInputs {
  devices?: IDevice[];
  people?: IPerson[];
  expiries?: IExpiry[];
  tiles?: IStatusTile[];
  audit?: IAuditEntry[];
  lastSync?: { at: string; ok: boolean; summary: string } | null;
}

/** How far ahead an expiry is worth interrupting somebody for. The Expiries screen shows ninety
 *  days; a bell that rang for everything inside a quarter would be ignored by Tuesday. */
export const EXPIRY_WARN_DAYS = 30;

const RANK: Record<AttentionSeverity, number> = { bad: 0, warn: 1, info: 2 };

/** A short, stable fingerprint of a set of ids, so a count-based item changes identity when its
 *  members do. djb2; not for security, only for telling two sets apart. */
export function fingerprint(ids: readonly string[]): string {
  const joined = [...ids].sort().join('|');
  let h = 5381;
  for (let i = 0; i < joined.length; i += 1) h = ((h << 5) + h + joined.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

const nameOf = (d: IDevice): string => (d.hostName && d.hostName.trim()) || d.tag;

function names(list: string[], limit = 4): string {
  const shown = list.slice(0, limit).join(', ');
  return list.length > limit ? `${shown} and ${list.length - limit} more` : shown;
}

export function buildAttention(input: AttentionInputs): AttentionItem[] {
  const out: AttentionItem[] = [];
  const devices = input.devices ?? [];
  const people = input.people ?? [];

  /* ── Sync ───────────────────────────────────────────────────────────── */

  if (input.lastSync && !input.lastSync.ok) {
    out.push({
      id: `sync:failed:${input.lastSync.at}`,
      severity: 'bad',
      group: 'Sync',
      icon: 'ti-refresh-alert',
      title: 'The last sync did not finish cleanly',
      detail: input.lastSync.summary,
      to: '/',
    });
  }

  /* Anything else a script has pushed to the dashboard and marked as trouble. The sync's own tile
     is covered above, so it is not repeated here. */
  for (const t of input.tiles ?? []) {
    if (t.tone !== 'bad' && t.tone !== 'warn') continue;
    if (/sync/i.test(t.label) || /sync/i.test(t.src)) {
      if (input.lastSync) continue;
    }
    out.push({
      id: `tile:${t.label}:${t.val}:${t.tone}`,
      severity: t.tone === 'bad' ? 'bad' : 'warn',
      group: 'Sync',
      icon: t.icon || 'ti-alert-triangle',
      title: t.label,
      detail: t.val,
      to: '/',
    });
  }

  /* ── Devices ────────────────────────────────────────────────────────── */

  for (const d of devices) {
    if (d.leftIntune) {
      out.push({
        id: `left-intune:${d.id}:${d.leftIntune}`,
        severity: 'warn',
        group: 'Devices',
        icon: 'ti-cloud-off',
        title: `${nameOf(d)} has left Intune`,
        detail: `Intune stopped listing it on ${fmtDate(d.leftIntune)}, so its compliance and encryption are no longer being checked.`,
        to: recordHref('/devices', d.tag),
      });
    }
    if (d.leftNinja) {
      out.push({
        id: `left-ninja:${d.id}:${d.leftNinja}`,
        severity: 'warn',
        group: 'Devices',
        icon: 'ti-plug-off',
        title: `${nameOf(d)} has left NinjaOne`,
        detail: `NinjaOne stopped reporting it on ${fmtDate(d.leftNinja)}. If it was replaced or renamed, find the new row and retire this one.`,
        to: recordHref('/devices', d.tag),
      });
    }
  }

  /* Counted, not listed: forty laptops with a stale flag would push everything else off the panel.
     Machines that have left Intune are excluded because their flag is frozen and already has its
     own item. */
  const live = devices.filter((d) => groupOf(d.type) === 'endpoint' && !d.leftIntune);
  const unencrypted = live.filter((d) => /not encrypted/i.test(d.enc));
  if (unencrypted.length > 0) {
    out.push({
      id: `not-encrypted:${fingerprint(unencrypted.map((d) => d.id))}`,
      severity: 'warn',
      group: 'Devices',
      icon: 'ti-lock-open',
      title: `${unencrypted.length} ${unencrypted.length === 1 ? 'device is' : 'devices are'} not encrypted`,
      detail: names(unencrypted.map(nameOf)),
      to: '/devices',
    });
  }
  const noncompliant = live.filter((d) => /^non-compliant$/i.test(d.compliance));
  if (noncompliant.length > 0) {
    out.push({
      id: `non-compliant:${fingerprint(noncompliant.map((d) => d.id))}`,
      severity: 'warn',
      group: 'Devices',
      icon: 'ti-shield-x',
      title: `${noncompliant.length} ${noncompliant.length === 1 ? 'device is' : 'devices are'} not compliant`,
      detail: names(noncompliant.map(nameOf)),
      to: '/devices',
    });
  }

  for (const e of input.audit ?? []) {
    if (e.act !== 'Device renamed') continue;
    out.push({
      id: `renamed:${e.obj}:${e.when}`,
      severity: 'info',
      group: 'Devices',
      icon: 'ti-writing',
      title: `${e.obj} has been renamed`,
      detail: 'Same serial, new name. It is the same machine and kept its row.',
      to: recordHref('/devices', e.obj),
    });
  }

  /* ── People ─────────────────────────────────────────────────────────── */

  const toSetUp = people.filter(isOnboarding);
  if (toSetUp.length > 0) {
    out.push({
      id: `to-set-up:${fingerprint(toSetUp.map((p) => p.id))}`,
      severity: 'warn',
      group: 'People',
      icon: 'ti-user-plus',
      title: `${toSetUp.length} ${toSetUp.length === 1 ? 'account is' : 'accounts are'} waiting to be set up`,
      detail: names(toSetUp.map((p) => p.name)),
      to: '/onboarding',
    });
  }
  const unwelcomed = people.filter((p) => needsWelcome(p));
  if (unwelcomed.length > 0) {
    out.push({
      id: `to-welcome:${fingerprint(unwelcomed.map((p) => p.id))}`,
      severity: 'info',
      group: 'People',
      icon: 'ti-mail',
      title: `${unwelcomed.length} ${unwelcomed.length === 1 ? 'person has' : 'people have'} not been welcomed`,
      detail: names(unwelcomed.map((p) => p.name)),
      to: '/onboarding',
    });
  }
  const leaving = people.filter(isOffboarding);
  if (leaving.length > 0) {
    out.push({
      id: `leaving:${fingerprint(leaving.map((p) => p.id))}`,
      severity: 'warn',
      group: 'People',
      icon: 'ti-user-minus',
      title: `${leaving.length} ${leaving.length === 1 ? 'person is' : 'people are'} leaving`,
      detail: names(leaving.map((p) => p.name)),
      to: '/offboarding',
    });
  }

  /* ── Expiries ───────────────────────────────────────────────────────── */

  for (const e of input.expiries ?? []) {
    if (e.days > EXPIRY_WARN_DAYS) continue;
    out.push({
      id: `expiry:${e.id}:${e.due}`,
      severity: e.days < 0 ? 'bad' : 'warn',
      group: 'Expiries',
      icon: e.icon || 'ti-calendar-exclamation',
      title: e.label,
      detail:
        e.days < 0
          ? `${e.detail}. Overdue since ${fmtDate(e.due)}.`
          : `${e.detail}. Due ${fmtDate(e.due)}.`,
      to: e.href,
    });
  }

  return out.sort(
    (a, b) => RANK[a.severity] - RANK[b.severity] || a.group.localeCompare(b.group) || a.title.localeCompare(b.title),
  );
}

/* ── What has been dismissed ───────────────────────────────────────────────
 *
 * Shared, so one person dismissing an item clears it for everybody, and kept in the existing
 * settings list as one JSON value: no new list to provision. A map of id to the day it was
 * dismissed, so old entries can be dropped. Two people dismissing at once is handled by merging,
 * not by last-write-wins (see mergeDismissed). */

export const DISMISSED_KEY = 'attention.dismissed';

/** How long a dismissal is remembered. An item whose cause has gone never comes back to look this
 *  up, and one whose cause is still there after three months deserves to be raised again. */
export const DISMISSED_DAYS = 90;

export type Dismissed = Record<string, string>;

export function parseDismissed(raw: string | undefined): Dismissed {
  if (!raw) return {};
  try {
    const v = JSON.parse(raw) as unknown;
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
    const out: Dismissed = {};
    for (const [k, d] of Object.entries(v as Record<string, unknown>)) {
      if (typeof d === 'string') out[k] = d;
    }
    return out;
  } catch {
    return {};
  }
}

/** Union of what is stored now and what this person is dismissing, with old entries dropped.
 *  Merged from a fresh read rather than from what the page had, so somebody else's dismissal made
 *  a minute ago is not undone by this one. */
export function mergeDismissed(stored: Dismissed, ids: readonly string[], now: Date): Dismissed {
  const today = now.toISOString().slice(0, 10);
  const cutoff = new Date(now.getTime() - DISMISSED_DAYS * 86_400_000).toISOString().slice(0, 10);
  const out: Dismissed = {};
  for (const [k, d] of Object.entries(stored)) if (d >= cutoff) out[k] = d;
  for (const id of ids) if (!out[id]) out[id] = today;
  return out;
}

export function visibleAttention(items: AttentionItem[], dismissed: Dismissed): AttentionItem[] {
  return items.filter((i) => !dismissed[i.id]);
}
